import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CsvParserService } from 'shared/services/csv-parser.service';

@Component({
  selector: 'app-bulk-payment-upload-step',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './upload-step.component.html'
})
export class BulkPaymentUploadStepComponent implements OnInit {
  // Restores the "file selected" display when this component is re-created
  // after navigating back to Step 1 (the wizard destroys/recreates it via
  // @if, so its own fileName/rowCount would otherwise reset even though the
  // parent still holds the parsed data and keeps "Next" enabled).
  @Input() initialFileName: string | null = null;
  @Input() initialRowCount: number | null = null;

  @Output() csvUploaded = new EventEmitter<{ fileName: string; rowCount: number; content: string }>(); // Emit raw CSV content
  @Output() csvCleared = new EventEmitter<void>();

  uploadError: string | null = null;
  fileName: string | null = null;
  isDragging = false;
  isProcessing = false;
  rowCount = 0;

  constructor(private csvParser: CsvParserService) {}

  ngOnInit(): void {
    if (this.initialFileName) {
      this.fileName = this.initialFileName;
      this.rowCount = this.initialRowCount ?? 0;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private async handleFile(file: File): Promise<void> {
    this.uploadError = null;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.uploadError = 'Please select a CSV file';
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      this.uploadError = 'File size exceeds maximum allowed size of 5MB';
      return;
    }

    this.isProcessing = true;
    this.fileName = file.name;

    try {
      const content = await this.csvParser.readFileAsText(file);
      
      // Count rows
      const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');
      this.rowCount = lines.length > 0 ? lines.length - 1 : 0; // Exclude header

      // Emit raw content
      this.csvUploaded.emit({ fileName: file.name, rowCount: this.rowCount, content });
    } catch (error: any) {
      this.uploadError = error.message || 'Failed to read CSV file';
      this.fileName = null;
    } finally {
      this.isProcessing = false;
    }
  }

  clearFile(): void {
    this.fileName = null;
    this.rowCount = 0;
    this.csvCleared.emit();
  }
}